import { useEffect, useRef } from 'react'
import type { Vault } from '@/services/contract/contract.types.ts'
import { useProfile } from '@/hooks/useProfile.ts'
import { sendCountdownReminderToBenefactorWebplatform } from '@/services/notification/webplatformEmailSenders.ts'

const ONE_DAY_SECONDS = 86400

/**
 * Dashboard timer loop: when countdown is active, notify benefactor when ≤1 day
 * remains (once per session per vault).
 *
 * "Countdown Expired" emails to the beneficiary are intentionally NOT sent here;
 * they will be triggered by a contract-event webhook instead.
 */
export function useVaultCountdownEmailAlerts(
  vaults: Vault[],
  getTimeRemaining: (vaultId: bigint) => Promise<bigint>,
  enabled: boolean,
): void {
  const { getProfileByWallet } = useProfile()
  const reminderRef = useRef(new Set<number>())

  useEffect(() => {
    if (!enabled || vaults.length === 0) return

    const tick = async () => {
      const active = vaults.filter((v) => v.isCountdownActive)
      for (const v of active) {
        const vid = Number(v.id)
        try {
          const raw = await getTimeRemaining(v.id)
          if (raw == null) continue
          const remaining = raw
          const remNum = Number(remaining)

          if (
            remaining > 0n &&
            remNum <= ONE_DAY_SECONDS &&
            !reminderRef.current.has(vid)
          ) {
            const bfProf = await getProfileByWallet(v.benefactor)
            const benefactorEmail = bfProf?.email?.trim()
            if (!benefactorEmail) {
              reminderRef.current.add(vid)
              continue
            }
            const result = await sendCountdownReminderToBenefactorWebplatform({
              vaultId: vid,
              benefactorEmail,
              benefactorName: bfProf?.name ?? undefined,
              timeRemainingSeconds: remNum,
              notifyEmail: bfProf?.notify_email,
            })
            if (result?.success) reminderRef.current.add(vid)
          }
        } catch {
          /* ignore per vault */
        }
      }
    }

    const id = window.setInterval(() => {
      void tick()
    }, 1000)
    return () => window.clearInterval(id)
  }, [vaults, getTimeRemaining, enabled, getProfileByWallet])
}
